import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FullSequenceId } from 'src/lib/ActionSequence';
import { captureGenericError } from 'src/lib/sentry';
import platform from '@play-co/gcinstant';
import { State } from '../replicant/State';
import { getLocale } from '../replicant/getters/user';

export type SceneID =
  | 'debut'
  | 'livingRoom'
  | 'friendsLivingRoom'
  | 'diningRoom'
  | 'toiletRoom'
  | 'bedroom'
  | 'bathroom'
  | 'minigame'
  | '';

// 'left' has become a computed based state, checkable with isSceneLeft()
export type TransitionPhase = 'entering' | 'entered' | 'leaving';

export type Transition = {
  previousScene: SceneID;
  nextScene: SceneID;
  phase: TransitionPhase;
};

export type PopupOpts = {
  id: string;
  enabled: boolean;
  opts: any;
};

const initialState = {
  loading: 0,
  screenSize: {
    width: 0,
    height: 0,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },

  locale: 'ja',

  animating: false,

  isPetCrateOpening: false,

  togglePopup: { id: '', enabled: false, opts: {} as any },

  transition: {
    previousScene: '' as SceneID,
    nextScene: '' as SceneID,
    phase: 'entered' as TransitionPhase,
  },

  actionSequenceWorking: false,
  actionSequenceId: undefined as FullSequenceId,

  stack: '',
  stackRoot: '',
  stackPopups: '',
  lastPopupClosedTime: 0,
  currentPopup: '',
  visibleStats: '',
  previousVisibleStats: undefined as string,
};

const slice = createSlice({
  name: 'ui',

  initialState,
  reducers: {
    setScreenSize: (
      state,
      {
        payload,
      }: PayloadAction<{
        width: number;
        height: number;
        top: number;
        bottom: number;
        left: number;
        right: number;
      }>,
    ) => {
      state.screenSize = payload;
    },

    showLocale: (state, { payload }: PayloadAction<string>) => {
      state.locale = payload;
    },
    startSceneTransition: (state, { payload }: PayloadAction<SceneID>) => {
      const { previousScene, nextScene, phase } = state.transition;

      // Cannot start a transition while something is animating
      if (state.animating) return;

      // Cannot start a transition to the same scene we currently are
      if (nextScene === payload) return;

      // Cannot start a transition while another transition is already in place,
      // unless we are trying to reload the scene
      const isReload =
        nextScene === '' && previousScene === payload && phase === 'leaving';

      if (!isReload && phase !== 'entered') {
        throw new Error(
          `Cannot start a transition while another transition is already in place. Previous scene: ${previousScene}. Phase: ${phase}. Next scene: '${nextScene}'. Payload: ${payload}`,
        );
      }

      state.transition.previousScene = nextScene;
      state.transition.nextScene = payload;
      state.transition.phase = 'leaving';

      // UI breadcrumb stack; used exclusively for purchase analytics
      state.stack = payload;
      state.stackRoot = payload;
    },

    setTransitionPhase: (
      state,
      { payload }: PayloadAction<Exclude<TransitionPhase, 'leaving'>>,
    ) => {
      state.transition.phase = payload;
    },

    overwritePreviousScene: (state, { payload }: PayloadAction<SceneID>) => {
      state.transition.previousScene = payload;
    },

    togglePopup: (state, { payload }: PayloadAction<PopupOpts>) => {
      state.togglePopup = payload;

      // UI breadcrumb stack; used exclusively for purchase analytics
      const popupId = payload.id;
      const popupName = '.' + popupId;

      if (popupId === 'popupShopPurchase') {
        // "Thank you for the purchase" popup
        // We need to ignore this
        // For PurchaseTapped/PurchaseSuccess breadcrumb consistency
        return;
      }

      if (payload.enabled) {
        // We don't have a popup hierarchy system
        // When a popup closes, the other one opens right after
        // We determine hierarchy by timing which is kinda hacky
        if (Date.now() - state.lastPopupClosedTime < 250) {
          state.stack = state.stackRoot + state.stackPopups;
        } else {
          state.stackPopups = '';
        }

        // Add breadcrumb
        state.stack += popupName;

        state.currentPopup = popupId;
      } else {
        // Remove breadcrumb
        state.stack = state.stackRoot;

        // Save last popup in case we need it later
        state.lastPopupClosedTime = Date.now();
        if (state.stackPopups.indexOf(popupName) === -1) {
          state.stackPopups += popupName;
        }
      }
    },

    setAnimating: (state, { payload }: PayloadAction<boolean>) => {
      state.animating = payload;
    },

    showLoading: (state) => {
      ++state.loading;
    },
    hideLoading: (state) => {
      --state.loading;

      // TODO Fix this! :(
      if (state.loading < 0) {
        state.loading = 0;
        captureGenericError('Invalid loading state', null);
      }
    },

    setActionSequenceWorking: (
      state,
      {
        payload,
      }: PayloadAction<
        | { working: true; sequenceId: FullSequenceId }
        | { working: false; sequenceId?: undefined }
      >,
    ) => {
      state.actionSequenceWorking = payload.working;
      state.actionSequenceId = payload.sequenceId;
    },

    setVisibleStats: (state, { payload }: PayloadAction<string>) => {
      if (state.previousVisibleStats) return;
      state.visibleStats = payload;
    },

    setTempVisibleStats: (state, { payload }: PayloadAction<string>) => {
      if (state.previousVisibleStats) return;
      state.previousVisibleStats = state.visibleStats;
      state.visibleStats = payload;
    },

    clearTempVisibleStats: (state) => {
      if (!state.previousVisibleStats) return;
      state.visibleStats = state.previousVisibleStats;
      delete state.previousVisibleStats;
    },

    setPetCrateOpening: (state, { payload }: PayloadAction<boolean>) => {
      state.isPetCrateOpening = payload;
    },
  },

  extraReducers: {
    RESET_APP: (_, { payload }: PayloadAction<State>) => ({
      ...initialState,
      visibleStats: platform.playerID,
      locale: getLocale(payload),
    }),
  },
});

export const {
  showLocale,

  setScreenSize,

  startSceneTransition,
  setTransitionPhase,
  overwritePreviousScene,

  togglePopup,

  setAnimating,
  showLoading,
  hideLoading,
  setVisibleStats,
  setTempVisibleStats,
  clearTempVisibleStats,

  setPetCrateOpening,

  setActionSequenceWorking,
} = slice.actions;
export default slice.reducer;
